import apiClient, { getApiErrorMessage } from './apiClient';
import { buildReadyAppFqdn, READY_APP_DOMAIN_SUFFIX } from '../config/readyApps';

export { READY_APP_DOMAIN_SUFFIX, buildReadyAppFqdn } from '../config/readyApps';

const unwrapApiData = (data) => data?.data || data?.Data || data;
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (Array.isArray(unwrapped?.items)) return unwrapped.items;
  if (Array.isArray(unwrapped?.Items)) return unwrapped.Items;
  return [];
};

const normalizeVolume = (item) => ({
  name: item?.name || item?.Name || '',
  purposeFa: item?.purposeFa || item?.PurposeFa || '',
  purposeEn: item?.purposeEn || item?.PurposeEn || ''
});

export const normalizeReadyApp = (item) => ({
  key: item?.key || item?.Key || '',
  titleFa: item?.titleFa || item?.TitleFa || '',
  titleEn: item?.titleEn || item?.TitleEn || '',
  descriptionFa: item?.descriptionFa || item?.DescriptionFa || '',
  descriptionEn: item?.descriptionEn || item?.DescriptionEn || '',
  icon: item?.icon || item?.Icon || '',
  iconUrl: item?.iconUrl || item?.IconUrl || '',
  screenshotUrl: item?.screenshotUrl || item?.ScreenshotUrl || '',
  vendorName: item?.vendorName || item?.VendorName || '',
  vendorUrl: item?.vendorUrl || item?.VendorUrl || '',
  requiredPackageSlug: String(item?.requiredPackageSlug || item?.RequiredPackageSlug || '').toLowerCase(),
  allowed: Boolean(item?.allowed ?? item?.Allowed),
  kind: item?.kind || item?.Kind || 'Service',
  defaultCpu: Number(item?.defaultCpu ?? item?.DefaultCpu ?? 1),
  defaultMemoryMb: Number(item?.defaultMemoryMb ?? item?.DefaultMemoryMb ?? 1024),
  minCpu: Number(item?.minCpu ?? item?.MinCpu ?? 0.25),
  maxCpu: Number(item?.maxCpu ?? item?.MaxCpu ?? 8),
  minMemoryMb: Number(item?.minMemoryMb ?? item?.MinMemoryMb ?? 256),
  maxMemoryMb: Number(item?.maxMemoryMb ?? item?.MaxMemoryMb ?? 8192),
  volumes: (item?.volumes || item?.Volumes || []).map(normalizeVolume)
});

export const normalizeMarketplaceAppConnection = (item) => {
  if (!item) {
    return null;
  }

  return {
    host: item.host || item.Host || '',
    port: item.port ?? item.Port ?? null,
    username: item.username || item.Username || '',
    password: item.password || item.Password || '',
    databaseName: item.databaseName || item.DatabaseName || ''
  };
};

export const normalizeMarketplaceAppInstance = (item) => ({
  id: item?.id || item?.Id || '',
  catalogKey: item?.catalogKey || item?.CatalogKey || '',
  title: item?.title || item?.Title || '',
  titleFa: item?.titleFa || item?.TitleFa || '',
  titleEn: item?.titleEn || item?.TitleEn || '',
  subdomain: item?.subdomain || item?.Subdomain || '',
  fqdn: item?.fqdn || item?.Fqdn || '',
  url: item?.url || item?.Url || '',
  status: item?.status || item?.Status || '',
  error: item?.error || item?.Error || null,
  createdAt: item?.createdAt || item?.CreatedAt || null,
  updatedAt: item?.updatedAt || item?.UpdatedAt || null,
  kind: item?.kind || item?.Kind || 'Service',
  cpuLimit: Number(item?.cpuLimit ?? item?.CpuLimit ?? 0),
  memoryMb: Number(item?.memoryMb ?? item?.MemoryMb ?? 0),
  volumes: (item?.volumes || item?.Volumes || []).map(normalizeVolume),
  connection: normalizeMarketplaceAppConnection(item?.connection || item?.Connection),
  publicPort: item?.publicPort ?? item?.PublicPort ?? null
});

export const normalizeMarketplaceAppStats = (item) => ({
  cpuLimit: Number(item?.cpuLimit ?? item?.CpuLimit ?? 0),
  memoryLimitMb: Number(item?.memoryLimitMb ?? item?.MemoryLimitMb ?? 0),
  cpuPercent: item?.cpuPercent ?? item?.CpuPercent ?? null,
  memoryUsedMb: item?.memoryUsedMb ?? item?.MemoryUsedMb ?? null,
  usageAvailable: Boolean(item?.usageAvailable ?? item?.UsageAvailable)
});

export const readyAppDisplayTitle = (instance, language) =>
  instance?.title
  || (language === 'fa' ? instance?.titleFa : instance?.titleEn)
  || instance?.titleFa
  || instance?.titleEn
  || '';

const READY_APP_CDN_ICONS = {
  redis: 'https://cdn.risman.pro/assets/ready-apps/redis/icon.png',
  pocketbase: 'https://cdn.risman.pro/assets/ready-apps/pocketbase/icon.png',
  excalidraw: 'https://cdn.risman.pro/assets/ready-apps/excalidraw/icon.png',
  mysql: 'https://cdn.risman.pro/assets/ready-apps/mysql/icon.png',
  n8n: 'https://cdn.risman.pro/assets/ready-apps/n8n/icon.png',
  metabase: 'https://cdn.risman.pro/assets/ready-apps/metabase/icon.png',
  supabase: 'https://cdn.risman.pro/assets/ready-apps/supabase/icon.png',
  jitsi: 'https://cdn.risman.pro/assets/ready-apps/jitsi/icon.png'
};

export const readyAppAssetUrl = (catalogKey, file) => {
  const key = String(catalogKey || '').trim().toLowerCase();
  if (file === 'icon.svg' && READY_APP_CDN_ICONS[key]) {
    return READY_APP_CDN_ICONS[key];
  }

  return `/ready-apps/${encodeURIComponent(key)}/${file}`;
};

export const fetchReadyAppsCatalogApi = async () => {
  try {
    const { data } = await apiClient.get('/marketplace-apps/catalog');
    const payload = unwrapApiData(data);
    const items = Array.isArray(payload)
      ? payload
      : payload?.items || payload?.Items || [];
    return {
      domainSuffix: READY_APP_DOMAIN_SUFFIX,
      urlScheme: payload?.urlScheme || payload?.UrlScheme || 'http',
      isConfigured: payload?.isConfigured ?? payload?.IsConfigured ?? true,
      items: items.map(normalizeReadyApp).filter((item) => item.key)
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load ready apps.'));
  }
};

export const fetchMarketplaceAppsApi = async () => {
  try {
    const { data } = await apiClient.get('/marketplace-apps');
    return unwrapApiList(data).map(normalizeMarketplaceAppInstance).filter((item) => item.id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load app instances.'));
  }
};

export const fetchMarketplaceAppApi = async (id) => {
  try {
    const { data } = await apiClient.get(`/marketplace-apps/${encodeURIComponent(id)}`);
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load app instance.'));
  }
};

export const checkMarketplaceDomainApi = async (subdomain) => {
  try {
    const { data } = await apiClient.get('/marketplace-apps/domain-availability', {
      params: { subdomain }
    });
    const payload = unwrapApiData(data);
    const label = payload?.subdomain || payload?.Subdomain || subdomain;
    return {
      subdomain: label,
      fqdn: buildReadyAppFqdn(label),
      available: Boolean(payload?.available ?? payload?.Available)
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Domain availability check failed.'));
  }
};

export const createMarketplaceAppApi = async ({ catalogKey, subdomain, title, cpuLimit, memoryMb }) => {
  try {
    const { data } = await apiClient.post('/marketplace-apps', {
      CatalogKey: catalogKey,
      Subdomain: subdomain,
      Title: title,
      CpuLimit: cpuLimit,
      MemoryMb: memoryMb
    });
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to deploy app.'));
  }
};

export const renameMarketplaceAppApi = async (id, title) => {
  try {
    const { data } = await apiClient.patch(`/marketplace-apps/${encodeURIComponent(id)}`, {
      Title: title
    });
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to rename app.'));
  }
};

export const updateMarketplaceAppResourcesApi = async (id, { cpuLimit, memoryMb }) => {
  try {
    const { data } = await apiClient.patch(`/marketplace-apps/${encodeURIComponent(id)}/resources`, {
      CpuLimit: cpuLimit,
      MemoryMb: memoryMb
    });
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to update resources.'));
  }
};

export const changeMarketplaceAppDomainApi = async (id, subdomain) => {
  try {
    const { data } = await apiClient.patch(`/marketplace-apps/${encodeURIComponent(id)}/domain`, {
      Subdomain: subdomain
    });
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to change domain.'));
  }
};

export const startMarketplaceAppApi = async (id) => {
  try {
    const { data } = await apiClient.post(`/marketplace-apps/${encodeURIComponent(id)}/start`);
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to start app.'));
  }
};

export const stopMarketplaceAppApi = async (id) => {
  try {
    const { data } = await apiClient.post(`/marketplace-apps/${encodeURIComponent(id)}/stop`);
    return normalizeMarketplaceAppInstance(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to stop app.'));
  }
};

export const deleteMarketplaceAppApi = async (id) => {
  try {
    await apiClient.delete(`/marketplace-apps/${encodeURIComponent(id)}`);
    return id;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete app.'));
  }
};

export const fetchMarketplaceAppLogsApi = async (id, lines = 500) => {
  try {
    const { data } = await apiClient.get(`/marketplace-apps/${encodeURIComponent(id)}/logs`, {
      params: { lines }
    });
    const payload = unwrapApiData(data);
    return payload?.logs || payload?.Logs || '';
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load logs.'));
  }
};

export const fetchMarketplaceAppStatsApi = async (id) => {
  try {
    const { data } = await apiClient.get(`/marketplace-apps/${encodeURIComponent(id)}/stats`);
    return normalizeMarketplaceAppStats(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load stats.'));
  }
};
